import { type FC } from "react";

import { type ReplayContextSummary, shortSha } from "./observabilityModel";

export const CompositionContextHeader: FC<{
  cacheBoundaryIndex: number | null;
  localRowCount: number;
  replay: ReplayContextSummary | null;
}> = ({ cacheBoundaryIndex, localRowCount, replay }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-2">
    <div className="min-w-0">
      <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-400">
        {replay
          ? `effective context · ${replay.effectiveMessageCount} durable messages`
          : `composition · ${localRowCount} messages`}
      </span>
      {replay && (
        <span className="mt-1 block font-mono text-[9px] uppercase tracking-widest text-indigo-500 dark:text-indigo-300">
          {replay.replayedPrefixMessageCount} replayed prefix +{" "}
          {replay.durableDeltaMessageCount} durable delta ·{" "}
          {replay.localDeltaInputMessageCount} local input · {localRowCount}{" "}
          captured rows
        </span>
      )}
    </div>
    {cacheBoundaryIndex !== null && (
      <span className="shrink-0 font-mono text-[10px] text-amber-600 dark:text-amber-400">
        cache boundary at seq {cacheBoundaryIndex}
      </span>
    )}
  </div>
);

export const ReplayPrefixBand: FC<{ replay: ReplayContextSummary | null }> = ({
  replay,
}) => {
  if (!replay) {
    return null;
  }
  return (
    <div className="flex min-w-0 items-center gap-2 border-b border-indigo-100 bg-indigo-50/60 px-4 py-2 font-mono text-[10px] dark:border-indigo-900/40 dark:bg-indigo-950/20">
      <span className="w-6 shrink-0 text-indigo-400">srv</span>
      <span className="hidden w-14 shrink-0 uppercase tracking-wider text-indigo-500 sm:inline">
        replay
      </span>
      <span className="w-24 shrink-0 truncate text-indigo-700 dark:text-indigo-300 sm:w-32">
        replayed_prefix
      </span>
      <span className="min-w-0 flex-1 text-indigo-700 dark:text-indigo-300">
        {replay.replayedPrefixMessageCount} durable messages restored
        server-side via {replay.contextReplayProvider ?? "server_state_replay"}
      </span>
      {replay.contextReplayIdentityHash && (
        <span className="hidden w-16 shrink-0 text-right text-indigo-400 sm:inline">
          {shortSha(replay.contextReplayIdentityHash, 6)}
        </span>
      )}
    </div>
  );
};

export const ReplayDiffBanner: FC<{
  a: ReplayContextSummary | null;
  b: ReplayContextSummary | null;
}> = ({ a, b }) => {
  if (!a && !b) {
    return null;
  }
  return (
    <div className="border-t border-indigo-100 bg-indigo-50/60 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-indigo-700 dark:border-indigo-900/40 dark:bg-indigo-950/20 dark:text-indigo-300">
      replayed prefixes are summarized, not expanded; the rows below are only
      the locally composed delta sent on the wire.
    </div>
  );
};

export const ReplayDiffPrefixRows: FC<{
  a: ReplayContextSummary | null;
  b: ReplayContextSummary | null;
}> = ({ a, b }) => {
  if (!a && !b) {
    return null;
  }
  return (
    <div className="grid grid-cols-1 divide-y divide-indigo-100 border-t border-indigo-100 bg-indigo-50/40 font-mono text-[10px] sm:grid-cols-2 sm:divide-x sm:divide-y-0 dark:divide-indigo-900/40 dark:border-indigo-900/40 dark:bg-indigo-950/10">
      <ReplayDiffPrefixCell replay={a} side="A" />
      <ReplayDiffPrefixCell replay={b} side="B" />
    </div>
  );
};

const ReplayDiffPrefixCell: FC<{
  replay: ReplayContextSummary | null;
  side: "A" | "B";
}> = ({ replay, side }) => (
  <div className="flex min-w-0 items-center gap-2 px-3 py-2 text-indigo-700 dark:text-indigo-300">
    <span className="w-3 shrink-0 text-indigo-400">{side}</span>
    {replay ? (
      <>
        <span className="w-24 shrink-0 truncate">replayed_prefix</span>
        <span className="min-w-0 flex-1 truncate">
          {replay.replayedPrefixMessageCount} durable messages · effective{" "}
          {replay.effectiveMessageCount}
        </span>
      </>
    ) : (
      <span className="min-w-0 flex-1 truncate text-slate-500">
        no replay prefix
      </span>
    )}
  </div>
);
